import { Dialog, DialogType, getTheme, PrimaryButton, Stack, Text } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';

export interface IOnCallEndedModalProps {
    isOpen: boolean;
    onNo: () => void;
    onYes: (durationMinutes: number) => void;
    onDismiss: () => void;
}

const DURATIONS = [5, 10, 15, 30, 45, 60] as const;
const AUTO_CLOSE_SECONDS = 60;

function getDurationLabel(minutes: number): string {
    switch (minutes) {
        case 5:
            return '5 min';
        case 10:
            return '10 min';
        case 15:
            return IvicosStrings.focusTime15Min;
        case 30:
            return IvicosStrings.focusTime30Min;
        case 45:
            return IvicosStrings.focusTime45Min;
        case 60:
            return IvicosStrings.focusTime1H;
        default:
            return `${minutes} min`;
    }
}

function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m} : ${String(s).padStart(2, '0')}`;
}

function DurationButton({
    minutes,
    selected,
    theme,
    onSelect
}: {
    minutes: number;
    selected: boolean;
    theme: ReturnType<typeof getTheme>;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            style={{
                padding: '10px 16px',
                borderRadius: 6,
                border: '1px solid ' + (selected ? theme.palette.themePrimary : theme.palette.neutralLight),
                backgroundColor: selected ? theme.palette.themePrimary : theme.palette.neutralLighter,
                color: selected ? theme.palette.white : theme.palette.black,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                minWidth: 72
            }}
        >
            {getDurationLabel(minutes)}
        </button>
    );
}

/**
 * Modal shown when on-call timer expires.
 * Asks "Are you still on the phone?", offers extend (For another + duration buttons), 60s auto-close countdown, No/Yes.
 * No or timeout → available. Yes → extend with selected duration, stay on-the-phone.
 */
const OnCallEndedModal: React.FC<IOnCallEndedModalProps> = ({ isOpen, onNo, onYes, onDismiss }) => {
    const theme = getTheme();
    const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number>(15);
    const [remainingSeconds, setRemainingSeconds] = useState(AUTO_CLOSE_SECONDS);

    useEffect(() => {
        if (!isOpen) return;
        setRemainingSeconds(AUTO_CLOSE_SECONDS);
        const id = setInterval(() => {
            setRemainingSeconds((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                    clearInterval(id);
                    onNo();
                    return 0;
                }
                return next;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [isOpen, onNo]);

    const handleNo = () => {
        onNo();
    };

    const handleYes = () => {
        onYes(selectedDurationMinutes);
    };

    const handleDismiss = () => {
        onNo();
        onDismiss();
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleDismiss}
            maxWidth="500px"
            minWidth="500px"
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.onCallEndedModalTitle,
                closeButtonAriaLabel: IvicosStrings.onCallEndedCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 20 }}>
                <Text variant="medium" style={{ color: theme.palette.neutralSecondary }}>
                    {IvicosStrings.onCallEndedForAnother}
                </Text>
                <Stack
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 12
                    }}
                >
                    {DURATIONS.map((min) => (
                        <DurationButton
                            key={min}
                            minutes={min}
                            selected={selectedDurationMinutes === min}
                            theme={theme}
                            onSelect={() => setSelectedDurationMinutes(min)}
                        />
                    ))}
                </Stack>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                        {IvicosStrings.onCallEndedAutoCloseLabel}
                    </Text>
                    <Text variant="large" style={{ fontWeight: 600 }}>
                        {formatCountdown(remainingSeconds)}
                    </Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        onClick={handleNo}
                        style={{
                            padding: '8px 20px',
                            borderRadius: 6,
                            border: '1px solid ' + theme.palette.neutralLight,
                            backgroundColor: 'transparent',
                            color: theme.palette.black,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 500
                        }}
                    >
                        {IvicosStrings.onCallEndedNo}
                    </button>
                    <PrimaryButton text={IvicosStrings.onCallEndedYes} onClick={handleYes} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default OnCallEndedModal;
